import React from 'react';
import { ImageFadeIn } from '../components/animations/ImageFadeIn';
import { ScrollReveal } from '../components/animations/ScrollReveal';
import { useDonateByMail } from '../contexts/DonateByMailContext';
import { Target, School, Home, Building, Siren, Building2, Heart, Users, ShieldCheck, AlertTriangle } from 'lucide-react';
const TrainingCenterPage = () => {
  const {
    openDonateByMail
  } = useDonateByMail();
  return <div className="w-full min-h-screen bg-white" data-id="element-1169" data-mp="2m34te4dng57c">
      {/* Hero Section - Reduced height */}
      <div className="relative min-h-[50vh] flex items-center" data-id="element-1170" data-mp="2h5d0p856kf23">
        <div className="absolute inset-0 z-0" data-id="element-1171" data-mp="2vv25rege937n">
          <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/dCpAxqefTErEsMjEUAy1YR/istockphoto-1738959959-612x612.jpg" alt="Israeli military personnel walking with flag" className="w-full h-full object-cover" data-id="element-1172" data-mp="2vwvdyy6nnvhr" />
          <div className="absolute inset-0 bg-black/60" data-id="element-1173" data-mp="2zxp0bn4g928j"></div>
        </div>
        <div className="relative z-10 w-full pt-36 sm:pt-52 md:pt-56 pb-12 sm:pb-16" data-id="element-1174" data-mp="1ra50tz604y9">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1175" data-mp="6p1uzig91gzt">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-3 sm:mb-4" data-id="element-1176" data-mp="6remam7wd3pj">
              Regional Civilian Training Centers
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1177" data-mp="gp2ilgl5ij9t">
              Building a safer Israel through education, preparation, and
              community resilience
            </p>
          </div>
        </div>
      </div>
      {/* Mission Statement - Reduced padding */}
      <ScrollReveal animation="fade" data-id="element-1178" data-mp="ze93995i9ky9">
        <div className="bg-blue-50 py-10 sm:py-16" data-id="element-1179" data-mp="zd7i2burzi2t">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center" data-id="element-1180" data-mp="zg3hy5q6ogtw">
            <div className="inline-flex items-center justify-center mb-4" data-id="element-1181" data-mp="14f6l1kmlscz8">
              <Heart className="w-8 h-8 text-red-500 mr-3" data-id="element-1182" data-mp="18fysc8bx07sv" />
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight" data-id="element-1183" data-mp="35fbi5a2nhtzu">
                Our Mission
              </h2>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed mb-6 max-w-3xl mx-auto" data-id="element-1184" data-mp="30i1kx890se1g">
              The Samaria Regional Training Center (SRTC) will serve as the
              first permanent hub for civilian security and emergency response
              training, offering free instruction to Israeli civilians. This
              state-of-the-art facility is designed to equip civilians with the
              critical, life-saving skills needed to survive—and thrive—in a
              volatile environment.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-1185" data-mp="z0ldnoemnk1">
              <div className="flex items-center" data-id="element-1186" data-mp="3t5xe42zdrnth">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1187" data-mp="22s6kgsjstn77">
                  <ShieldCheck className="w-5 h-5 text-blue-600" data-id="element-1188" data-mp="22ujc5xfczbxg" />
                </div>
                <span className="text-gray-700" data-id="element-1189" data-mp="1yt7k7l90iem3">
                  No or Minimal Cost Training for All Civilians
                </span>
              </div>
              <div className="flex items-center" data-id="element-1190" data-mp="1sp1b46r5fry7">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1191" data-mp="1sqvz5txpw06i">
                  <Users className="w-5 h-5 text-blue-600" data-id="element-1192" data-mp="1ewlb7etjoa1i" />
                </div>
                <span className="text-gray-700" data-id="element-1193" data-mp="1jvfvymb2o8o9">50,000 Trained Annually</span>
              </div>
              <div className="flex items-center" data-id="element-1194" data-mp="14y5mfihzpcv6">
                <div className="p-2 rounded-full bg-blue-100 mr-2" data-id="element-1195" data-mp="151jqqwa7rw59">
                  <Target className="w-5 h-5 text-blue-600" data-id="element-1196" data-mp="19zv0l70ie6nh" />
                </div>
                <span className="text-gray-700" data-id="element-1197" data-mp="vzk6zw7tedym">Professional Instructors</span>
              </div>
            </div>
            <p className="text-lg text-gray-700" data-id="element-1198" data-mp="10y58dyysz3on">
              No comparable center currently exists in Israel, and the
              development of this facility will establish a powerful and
              sustained capability to ensure the local population is fully
              prepared for all types of crisis situations.
            </p>
          </div>
        </div>
      </ScrollReveal>
      {/* Main Content - Reduced spacing between sections */}
      <div className="py-16 sm:py-24" data-id="element-1199" data-mp="laxlv5v9pef3">
        <div className="max-w-6xl mx-auto px-4 sm:px-6" data-id="element-1200" data-mp="l9wbz4pzmxyy">
          {/* Live-Fire Ranges Section */}
          <ScrollReveal animation="fade" data-id="element-1201" data-mp="h92ky2h9131u">
            <div className="mb-24" data-id="element-1202" data-mp="caiz022nnr9h">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1203" data-mp="cbkkz1l7ly5z">
                <div className="w-full md:w-3/5" data-id="element-1204" data-mp="7jaof2pl9zuq">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/k35j6YjqWVjKpJmHa5WRo3/000_QQ3AP-e1502868300864.webp" alt="Civilians practicing with firearms in a training environment" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1205" data-mp="7ihr0scismp9" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1206" data-mp="3kpsyd2f5uidk">
                  <div className="flex items-center mb-4" data-id="element-1207" data-mp="3ksfehqg4a67e">
                    <Target className="w-8 h-8 text-blue-500 mr-3" data-id="element-1208" data-mp="2078lpc9ew31f" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1209" data-mp="2574qhdpxnyi1">
                      Live-Fire Training Ranges
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1210" data-mp="2975ftteklu3v">
                    Our facility features three dedicated civilian training
                    ranges: a 100-meter rifle range and 2 25-meter pistol range.
                    Both are equipped with advanced lighting systems and moving
                    targets to create realistic training scenarios that prepare
                    civilians for real-world threats.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1211" data-mp="1varxki27pooc">
                    Unlike commercial ranges, our facilities are specifically
                    designed for civilian defense training, with instructors who
                    understand the unique challenges faced by non-military
                    personnel in crisis situations.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Classroom and Simulation Section */}
          <ScrollReveal animation="fade" data-id="element-1212" data-mp="2evc3szqlvb86">
            <div className="mb-24" data-id="element-1213" data-mp="2aus5vty7k3kz">
              <div className="flex flex-col md:flex-row-reverse gap-12 items-center" data-id="element-1214" data-mp="2avtk27ait66x">
                <div className="w-full md:w-3/5" data-id="element-1215" data-mp="1n5z7lqu45jn7">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/9kZniEp5pHeQCp7B21FM72/Screenshot_2025-06-07_at_6.21.43_PM.png" alt="Education and simulation training" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1216" data-mp="1n6shh0odcp9k" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1217" data-mp="37jzx9ilid6wt">
                  <div className="flex items-center mb-4" data-id="element-1218" data-mp="37mvsgz5z1hhk">
                    <School className="w-8 h-8 text-blue-500 mr-3" data-id="element-1219" data-mp="32koe62xo5pc2" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1220" data-mp="3b2ynsyl4ikpl">
                      Education & Simulation
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1221" data-mp="3f1z4obk1jd9y">
                    Our 20,000 square foot classroom complex houses
                    state-of-the-art MILO simulators that provide immersive,
                    scenario-based training in a controlled environment. These
                    advanced systems allow civilians to practice decision-making
                    and response techniques without risk.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1222" data-mp="3k2lspud2a8ne">
                    The educational curriculum combines theoretical knowledge
                    with practical application, ensuring that every participant
                    gains not just skills, but the confidence to use them
                    effectively in emergency situations.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Tactical Village Section */}
          <ScrollReveal animation="fade" data-id="element-1223" data-mp="2co9pszd8gls1">
            <div className="mb-24" data-id="element-1224" data-mp="27qq115iwsqlu">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1225" data-mp="27rrlg0ltepg8">
                <div className="w-full md:w-3/5" data-id="element-1226" data-mp="23po8t8netetd">
                  <ImageFadeIn src="https://cdn.magicpatterns.com/uploads/e43pnZZjr473Xb5M9jzvyi/3B33D28A-2AE4-4CE9-83F7-10F66D2901A1e9f91f72-3991-4691-ad21-7b3d91cac443.jpg" alt="Aerial view of Combat Town tactical training village" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1227" data-mp="23qzc77cy0kjy" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1228" data-mp="2mf4bo2mkhjcy">
                  <div className="flex items-center mb-4" data-id="element-1229" data-mp="1fzafzfx03bxq">
                    <Building className="w-8 h-8 text-blue-500 mr-3" data-id="element-1230" data-mp="1g0v9r3bhqquj" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1231" data-mp="1azfn56to0ca1">
                      Tactical Training Village
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1232" data-mp="3rmdzetxaeaql">
                    Our Combat Town training village replicates real urban
                    environments with integrated roadways, buildings, and
                    obstacles. This facility allows civilians to practice home
                    defense, community protection, and emergency evacuation in
                    realistic settings.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1233" data-mp="e667cn25b76r">
                    Participants learn to navigate complex scenarios including
                    multiple threats, civilian protection, and coordination with
                    neighbors—skills that have proven critical during actual
                    attacks on Israeli communities.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* Medical Training Section */}
          <ScrollReveal animation="fade" data-id="element-1234" data-mp="1e069zf0qn7s4">
            <div className="mb-24" data-id="element-1235" data-mp="1hzez26ku2hhc">
              <div className="flex flex-col md:flex-row-reverse gap-12 items-center" data-id="element-1236" data-mp="1i0go5eyop4i2">
                <div className="w-full md:w-3/5" data-id="element-1237" data-mp="1my054mmrnvdp">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/thhZHdVXq4qvhzzQjtRD9a/%D7%A6%D7%99%D7%9C%D7%95%D7%9D-%D7%99%D7%97%D7%99%D7%90%D7%9C-%D7%92%D7%95%D7%A8%D7%A4%D7%99%D7%99%D7%9F-127-1024x683.jpg" alt="Medical emergency response training" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1238" data-mp="1ry4jo7446g1a" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1239" data-mp="2ap5hgrti05j9">
                  <div className="flex items-center mb-4" data-id="element-1240" data-mp="2anubqmghsnpi">
                    <Siren className="w-8 h-8 text-blue-500 mr-3" data-id="element-1241" data-mp="2foi3jacjp8ks" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1242" data-mp="2kmtdrm6r5l3j">
                      Medical Emergency Response
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1243" data-mp="8xdfw4zx2u2d">
                    Our trauma-simulation lab features high-fidelity medical
                    manikins that replicate realistic injuries and physiological
                    responses. Civilians learn critical first-aid skills
                    including tourniquet application, wound packing, and
                    casualty evacuation.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1244" data-mp="mqbiabas1mff">
                    When emergency services are delayed—as often happens in
                    remote communities or during large-scale attacks—these
                    medical skills can save lives in the critical first minutes
                    after injury.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
          {/* All-Hazard Response Training Section */}
          <ScrollReveal animation="fade" data-id="element-1245" data-mp="2zx1c6e8df8mn">
            <div className="mb-24" data-id="element-1246" data-mp="2zy2vt73gd3fz">
              <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-1247" data-mp="34wnx79ufxt60">
                <div className="w-full md:w-3/5" data-id="element-1248" data-mp="34yqkvn84fa7k">
                  <ImageFadeIn src="https://uploadthingy.s3.us-west-1.amazonaws.com/ktjBBrrjxDcrx4BJceBNW7/Screenshot_2025-08-06_at_7.55.02_PM.png" alt="Emergency response personnel conducting search and rescue operations at a structural collapse" className="w-full h-auto rounded-xl shadow-xl" data-id="element-1249" data-mp="2q8jbdx03d1j0" />
                </div>
                <div className="w-full md:w-2/5" data-id="element-1250" data-mp="279p1p2fhir9p">
                  <div className="flex items-center mb-4" data-id="element-1251" data-mp="27azzmlm914sw">
                    <AlertTriangle className="w-8 h-8 text-blue-500 mr-3" data-id="element-1252" data-mp="2c91nrkyj7ub8" />
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-1253" data-mp="5h0uyj3mtlp6">
                      All-Hazard Response Training
                    </h2>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-1254" data-mp="1uynzst49icrv">
                    Our specialized facilities include basic training for
                    chemical-radiological defense, fully equipped safe rooms for
                    shelter-in-place training, and a dedicated area for
                    practicing evacuation procedures during fires, explosions,
                    and natural disasters.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-1255" data-mp="2dnuhcctbkyc3">
                    The center features a structural collapse simulator where
                    civilians learn basic search and rescue techniques. These
                    facilities enable civilians to practice earthquake response
                    protocols and coordinate with security operations during
                    emergencies.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
      {/* Accommodation Section */}
      <ScrollReveal animation="fade" data-id="element-1256" data-mp="3bjgfrt44jltw">
        <div className="bg-gray-50 py-10 sm:py-16" data-id="element-1257" data-mp="3biexp4ooxz2o">
          <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-1258" data-mp="3ggypvmtvo73h">
            <div className="text-center mb-8" data-id="element-1259" data-mp="3gjkx1lcuwkl6">
              <div className="inline-flex items-center justify-center mb-3" data-id="element-1260" data-mp="3lhw4xqjlcl0r">
                <Home className="w-7 h-7 text-blue-500 mr-2" data-id="element-1261" data-mp="3piy7ohcpvf7h" />
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-id="element-1262" data-mp="3pgbxsb2csnm3">
                  On-Site Accommodations
                </h2>
              </div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1263" data-mp="2xrs1pkabenaa">
                Supporting multi-day training programs for families and
                communities
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1264" data-mp="36kvavgdxgdz4">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-1265" data-mp="2a0mntanxq52m">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-1266" data-mp="2e39irkfwk641">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1267" data-mp="26jrtrrjw2o03">
                    Residential Facilities
                  </h3>
                  <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-1268" data-mp="21lqb37qdkqp5">
                    Our 25-bed dormitory includes 10 twin rooms for individual
                    participants and 5 queen rooms for couples, allowing
                    families to train together and build collective resilience.
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-1269" data-mp="1nmz1nymgjt7c">
                    The facility is designed to accommodate multi-day intensive
                    training programs, particularly for participants from remote
                    communities who would otherwise be unable to access quality
                    training.
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-1270" data-mp="zvbik2vftmfm">
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-full" data-id="element-1271" data-mp="zvtm7kkfj2xk">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1272" data-mp="q64lr9om0s8s">
                    Dining & Community Spaces
                  </h3>
                  <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-1273" data-mp="g9rs2bt2eee1">
                    Our full-service dining hall can serve up to 150 people,
                    supporting not just training participants but also community
                    events and emergency response coordination.
                  </p>
                  <p className="text-base text-gray-700 leading-relaxed" data-id="element-1274" data-mp="1dxjsdenf5vpu">
                    These spaces are designed to foster community connections
                    and build the social fabric that becomes crucial during
                    crisis situations, when neighbors must rely on each other.
                  </p>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* Impact & Capacity */}
      <ScrollReveal animation="fade" data-id="element-1275" data-mp="3hdx4fx4l22um">
        <div className="bg-israel-blue text-white py-10 sm:py-16" data-id="element-1276" data-mp="3hcvslqfqtcbu">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1277" data-mp="3cfdjo01q6x3u">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight" data-id="element-1278" data-mp="3cd9eles3bs1z">
              Our Impact
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10" data-id="element-1279" data-mp="3m55fexlk9w9w">
              <ScrollReveal animation="slide-up" delay={0.1} data-id="element-1280" data-mp="3m79ff4gcab4w">
                <div data-id="element-1281" data-mp="2yg42oy5p3yyt">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1282" data-mp="2yfkre0ajw8tr">7</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1283" data-mp="2thj8pgh1ebit">
                    Full-time Israeli instructors with ability to scale
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.2} data-id="element-1284" data-mp="33cuirlhi2uk8">
                <div data-id="element-1285" data-mp="mp0ez4oj50ii">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1286" data-mp="mogsrbrujpyo">50K</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1287" data-mp="ioy9ekfobmxy">
                    Civilians trained annually at minimal to no cost
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal animation="slide-up" delay={0.3} data-id="element-1288" data-mp="2h71qotf0yvhn">
                <div data-id="element-1289" data-mp="2m537f7ozaipy">
                  <div className="text-4xl sm:text-5xl font-bold mb-2" data-id="element-1290" data-mp="2m4ti7mbrcflb">3</div>
                  <p className="text-base opacity-90 font-light" data-id="element-1291" data-mp="2qvjnz0o3ikgj">
                    Future centers planned across Israel
                  </p>
                </div>
              </ScrollReveal>
            </div>
            <div data-id="element-1292" data-mp="2zvym50m5ru1g">
              <a href="/roadmap" className="inline-block px-6 py-3 bg-white text-israel-blue font-semibold rounded-lg hover:bg-gray-100 transition-colors text-base shadow-lg" data-id="element-1293" data-mp="2zvgihix62dji">
                View Our Implementation Timeline
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
      {/* CTA Section */}
      <ScrollReveal animation="fade" data-id="element-1294" data-mp="1fg4sy2fg62o2">
        <div className="py-10 sm:py-16 bg-white" data-id="element-1295" data-mp="1fh6e4zq6g5ji">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center" data-id="element-1296" data-mp="3sqo4l4zc74xh">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 tracking-tight" data-id="element-1297" data-mp="3ssr20a2tk0c8">
              Help Us Build This Vital Resource
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto" data-id="element-1298" data-mp="3jze0wkulpsf2">
              Your support can help us break ground on this life-saving facility
              sooner. Every day we wait is another day Israeli civilians remain
              vulnerable.
            </p>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-1299" data-mp="bi9jcdy7lnal">
              <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="inline-block px-6 py-3 bg-israel-blue text-white font-semibold rounded-lg hover:bg-israel-blue-light transition-colors text-base shadow-lg" data-id="element-1300" data-mp="bgyeeat129hw">
                Donate Online
              </a>
              <button type="button" onClick={openDonateByMail} className="inline-block px-6 py-3 bg-white text-israel-blue font-semibold rounded-lg border-2 border-israel-blue hover:bg-gray-50 transition-colors text-base" data-id="element-1301" data-mp="2md6wpc27m238">
                Donate by Mail
              </button>
              <a href="/support" className="inline-block px-6 py-3 bg-white text-israel-blue font-semibold rounded-lg border-2 border-israel-blue hover:bg-gray-50 transition-colors text-base" data-id="element-1302" data-mp="2j9uknlan3qrt">
                Support Our Mission
              </a>
            </div>
          </div>
        </div>
      </ScrollReveal>
    </div>;
};
export default TrainingCenterPage;